import { i18n } from "@lingui/core";
import { Trans } from "@lingui/macro";
import { ChevronDown } from "@winamp/icons/ChevronDown.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Dropdown } from "@winamp/ui/components/Dropdown/Dropdown.js";
import { DropdownButton } from "@winamp/ui/components/Dropdown/DropdownButton.js";
import { DropdownItemButton } from "@winamp/ui/components/Dropdown/DropdownItemButton.js";
import { DropdownMenu } from "@winamp/ui/components/Dropdown/DropdownMenu.js";
import { Link } from "@winamp/ui/components/Link/Link.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import { capitalize } from "radash";
import type { ComponentProps } from "react";
import { Form } from "react-router";
import { WinampCreators } from "~/assets/WinampCreators.tsx";
import { useOptionalAuth } from "~/modules/auth/hooks";
import { getLanguages } from "~/modules/lingui/lingui";
import { ThemeSwitch } from "~/modules/theme/theme";

export function Header({
  className,
  canLogout,
  disableRedirect,
  ...props
}: ComponentProps<"header"> & {
  disableRedirect?: boolean;
  canLogout?: boolean;
}) {
  const auth = useOptionalAuth();
  const languages = getLanguages();
  const language =
    languages.find((lang) => lang.key === i18n.locale) || languages[0];

  return (
    <header
      className={classNames(
        "grid grid-cols-[1fr_auto] gap-4 justify-between max-sm:items-start items-center px-3",
        className,
      )}
      {...props}
    >
      <Link
        to="/"
        disabled={disableRedirect}
        {...(!disableRedirect ? { reloadDocument: true } : {})}
      >
        <WinampCreators className="h-8 sm:h-10 transition-all" />
      </Link>
      <div className="flex flex-wrap gap-3 items-center justify-end w-full">
        <ThemeSwitch withLabel />
        <Dropdown>
          <DropdownButton
            variant="ghost"
            size="xs"
            className="w-fit justify-self-end"
          >
            <span className="max-sm:hidden">{language.label}</span>
            <span className="sm:hidden">{capitalize(language.key)}</span>
            <ChevronDown />
          </DropdownButton>
          <DropdownMenu>
            {languages.map((language) => (
              <DropdownItemButton
                key={language.key}
                to={`?lng=${language.key}`}
                reloadDocument
              >
                {language.label}
              </DropdownItemButton>
            ))}
          </DropdownMenu>
        </Dropdown>
        {auth && canLogout && (
          <Button form="logout" type="submit" variant="ghost" size="xs">
            <Trans>Log out</Trans>
          </Button>
        )}
      </div>
      <Form
        id="logout"
        method="post"
        action="/auth/logout"
        className="hidden"
      />
    </header>
  );
}
